import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from '../ui/button';

export const Hero = () => {
  const [email, setEmail] = useState('');

  return (
    <section 
      className="pt-24 pb-16 bg-white relative overflow-hidden"
      data-testid="hero-section"
    >
      {/* Decorative orange dots */}
      <div className="absolute top-32 right-20 orange-dot hidden lg:block" />
      <div className="absolute top-48 right-40 orange-dot hidden lg:block" />
      <div className="absolute bottom-40 left-20 orange-dot hidden lg:block" />

      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-[52px] font-bold text-[#1E293B] leading-[1.15] mb-6"
              data-testid="hero-headline"
            >
              Keep All Your Projects, Conversations, and Ideas in One Place
            </h1>
            
            <p className="text-lg text-[#64748B] mb-8 leading-relaxed">
              Stop juggling multiple tools. BoardChat unites project management and team 
              communication so your team can plan, execute, and chat — all in one simple platform.
            </p>

            {/* Email Signup */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="email-input flex-1 max-w-xs"
                data-testid="hero-email-input"
              />
              <Button 
                className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 font-medium"
                data-testid="hero-cta-primary"
              >
                Sign up - it's free!
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Watch Demo Link */}
            <a 
              href="#demo" 
              className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] transition-colors"
              data-testid="hero-watch-demo"
            >
              <div className="w-8 h-8 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                <Play className="w-4 h-4 text-[#F97316] fill-[#F97316]" />
              </div>
              <span className="text-sm font-medium">Watch the Demo</span>
            </a>

            <p className="mt-8 text-sm text-[#94A3B8]">
              BoardChat combines the best of <span className="text-[#1E293B] font-medium">Trello</span> and{' '}
              <span className="text-[#1E293B] font-medium">Slack</span> — without the switching or confusion.
            </p>
          </motion.div>

          {/* Right Column - Kanban + Chat Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
            data-testid="hero-visual"
          >
            <div className="kanban-preview p-4 relative">
              {/* Kanban Board */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {/* To Do Column */}
                <div className="kanban-column">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-[#94A3B8]" />
                    <span className="text-xs font-medium text-[#64748B]">To Do</span>
                  </div>
                  <div className="kanban-card">
                    <p className="text-sm text-[#1E293B] font-medium">Research competitors</p>
                  </div>
                  <div className="kanban-card">
                    <p className="text-sm text-[#1E293B] font-medium">Update roadmap</p>
                    <span className="text-xs text-[#94A3B8] mt-1 block">Dec 24</span>
                  </div>
                  <div className="kanban-card">
                    <p className="text-sm text-[#1E293B] font-medium">Plan Q1 sprint</p>
                  </div>
                </div>

                {/* In Progress Column */}
                <div className="kanban-column">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-[#2563EB]" />
                    <span className="text-xs font-medium text-[#2563EB]">In Progress</span>
                  </div>
                  <div className="kanban-card border-l-2 border-l-[#2563EB]">
                    <p className="text-sm text-[#1E293B] font-medium">Design system components</p>
                    <div className="flex items-center gap-1 mt-2">
                      <div className="avatar avatar-blue text-[10px]">JD</div>
                      <div className="avatar avatar-purple text-[10px]">MK</div>
                    </div>
                  </div>
                  <div className="kanban-card border-l-2 border-l-[#F97316]">
                    <p className="text-sm text-[#1E293B] font-medium">API Integration</p>
                  </div>
                </div>

                {/* Done Column */}
                <div className="kanban-column">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-[#22C55E]" />
                    <span className="text-xs font-medium text-[#22C55E]">Done ✓</span>
                  </div>
                  <div className="kanban-card opacity-70">
                    <p className="text-sm text-[#1E293B] font-medium">Brand guidelines</p>
                  </div>
                  <div className="kanban-card opacity-70">
                    <p className="text-sm text-[#1E293B] font-medium">User research</p>
                  </div>
                </div>
              </div>

              {/* Chat Overlay */}
              <div className="absolute -right-4 top-1/4 w-56 space-y-2 hidden lg:block">
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="chat-bubble"
                >
                  <p className="text-sm text-[#1E293B]">Great progress! 🎉</p>
                  <p className="text-xs text-[#64748B] mt-1">Let's review tomorrow</p>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                  className="flex items-start gap-2"
                >
                  <div className="avatar avatar-green text-[10px] flex-shrink-0">S</div>
                  <div className="chat-bubble">
                    <p className="text-sm text-[#1E293B]">On it! Starting now...</p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: 20, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ delay: 1.2 }}
                  className="chat-bubble highlight"
                >
                  <p className="text-sm text-[#F97316] font-medium">Need help?</p>
                  <p className="text-xs text-[#64748B] mt-1">I can assist 👋</p>
                </motion.div>
              </div>

              {/* Number indicators */}
              <div className="absolute -left-3 top-20 w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center shadow-lg">
                1
              </div>
              <div className="absolute -left-3 top-44 w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center shadow-lg">
                2
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
